import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Timeoff, TimeoffBalance, TimeoffBalanceHistory, TimeoffRequest, TimeoffStateType } from "../../../types";
import {
    createTimeOffRequest,
    getBalanceByCategory,
    getEmployeeTimeoffBalanceHistoryList,
    getEmployeeTimeoffOverview,
    getEmployeeTimeoffRequestsList,
    getManagerTimeoffEmployees,
    getTimeoffEarningCodes,
    getTimeoffPolicyList,
    getTimeoffRequestDetails,
    getTimeOffRequestList,
    updateTimeoffPolicy,
    updateTimeoffRequest,
} from "./api";

const initialState: TimeoffStateType = {
    fetchedTimeoffRequests: [],
    isLoadingTimeOffRequests: false,

    isUpdateTimeoffMode: false,

    isLoadingTimeoffPolicies: false,
    timeoffPolicies: [],

    isLoadingTimeoffEarningCodes: false,
    timeoffEarningCodes: [],

    isUpdateMode: false,
    isUpdatingTimeoff: false,

    isLoadingTodayTimeoffRequest: false,

    isLoadingTimeoffDetails: false,
    timeoffDetails: undefined,

    isLoadingMobileCategoryRequests: false,
    isLoadingMoreMobileCategoryRequests: false,
    mobileCategoryRequests: [],

    isLoadingMobileCategoryOverview: false,

    isLoadingMobileCategoryBalanceHistory: false,
    isLoadingMoreMobileCategoryBalanceHistory: false,
    mobileCategoryBalanceHistory: [],

    isFetchingManagerTimeoffEmployees: false,
    managerTimeoffEmployeeRecords: [],
};

const isNextPage = (page?: number) => Number(page ?? 1) > 1;

const mergeUniqueTimeoffs = (existing: Timeoff[], incoming: Timeoff[]) => {
    const seen = new Set(existing.map((item) => item.time_off_request_id).filter(Boolean));
    const next = [...existing];

    incoming.forEach((item) => {
        if (!item.time_off_request_id || !seen.has(item.time_off_request_id)) {
            next.push(item);
            if (item.time_off_request_id) {
                seen.add(item.time_off_request_id);
            }
        }
    });

    return next;
};

const mergeUniqueBalanceHistory = (existing: TimeoffBalanceHistory[], incoming: TimeoffBalanceHistory[]) => {
    const getRecordId = (item: TimeoffBalanceHistory) =>
        item.os_emp_time_off_balance_history || item.balance_history_id;

    const seen = new Set(existing.map((item) => getRecordId(item)).filter(Boolean));
    const next = [...existing];

    incoming.forEach((item) => {
        const recordId = getRecordId(item);

        if (!recordId || !seen.has(recordId)) {
            next.push(item);
            if (recordId) {
                seen.add(recordId);
            }
        }
    });

    return next;
};

const timeoffState = createSlice({
    name: "timeoff",
    initialState,
    reducers: {
        setRequestStartDate: (state, action) => {
            state.selectedRequestStartDate = action.payload;
        },

        setRequestEndDate: (state, action) => {
            state.selectedRequestEndDate = action.payload;
        },

        clearRequestDates: (state) => {
            state.selectedRequestStartDate = undefined;
            state.selectedRequestEndDate = undefined;
        },

        setSelectedTimeoffForView: (state, action) => {
            state.selectedTimeoffForView = action.payload;
        },

        clearSelectedTimeoffForView: (state) => {
            state.selectedTimeoffForView = undefined;
        },

        setTimeoffUpdateMode: (state, action: PayloadAction<boolean>) => {
            state.isUpdateTimeoffMode = action.payload;
        },

        clearTimeoffUpdateMode: (state) => {
            state.isUpdateTimeoffMode = false;
        },

        setTimeoffToUpdate: (state, action: PayloadAction<Timeoff>) => {
            state.timeoffToUpdate = action.payload;
        },

        clearTimeoffToUpdate: (state) => {
            state.timeoffToUpdate = undefined;
        },

        resetMobileTimeoffCategoryDetail: (state) => {
            state.mobileCategoryRequests = [];
            state.mobileCategoryRequestsMeta = undefined;
            state.mobileCategoryRequestsError = undefined;
            state.mobileCategoryBalanceHistory = [];
            state.mobileCategoryBalanceHistoryMeta = undefined;
            state.mobileCategoryBalanceHistoryError = undefined;
            state.mobileTimeoffCategory = undefined;
            state.mobileTimeoffCategoryBalance = undefined;
            state.mobileTimeoffCategorySummary = undefined;
            state.mobileCategoryOverviewError = undefined;
            state.isLoadingMobileCategoryRequests = false;
            state.isLoadingMoreMobileCategoryRequests = false;
            state.isLoadingMobileCategoryOverview = false;
            state.isLoadingMobileCategoryBalanceHistory = false;
            state.isLoadingMoreMobileCategoryBalanceHistory = false;
        },
    },
    extraReducers: {
        [getTimeOffRequestList.pending.type]: (state) => {
            state.isLoadingTimeOffRequests = true;
            state.fetchedTimeoffRequests = [];
        },

        [getTimeOffRequestList.fulfilled.type]: (state, { payload }) => {
            state.isLoadingTimeOffRequests = false;
            state.fetchedTimeoffRequests = payload.data;
        },

        [getTimeOffRequestList.rejected.type]: (state) => {
            state.isLoadingTimeOffRequests = false;
            state.fetchedTimeoffRequests = [];
        },

        [getBalanceByCategory.fulfilled.type]: (state, { payload }) => {
            state.timeOffBalanceByCategory = payload
                .map((item: TimeoffBalance) => {
                    return {
                        ...item,
                        max: parseFloat(item.max!).toFixed(2),
                    };
                })
                .filter((item: TimeoffBalance) => !isNaN(Number(item.max!)));
        },

        [getBalanceByCategory.rejected.type]: (state) => {
            state.timeOffBalanceByCategory = undefined;
        },

        [getBalanceByCategory.pending.type]: (state) => {
            state.timeOffBalanceByCategory = undefined;
        },

        [getEmployeeTimeoffOverview.pending.type]: (state) => {
            state.isLoadingMobileCategoryOverview = true;
            state.mobileCategoryOverviewError = undefined;
        },

        [getEmployeeTimeoffOverview.fulfilled.type]: (state, { payload }) => {
            state.isLoadingMobileCategoryOverview = false;
            state.mobileTimeoffCategory = payload.category;
            state.mobileTimeoffCategoryBalance = payload.balance;
            state.mobileTimeoffCategorySummary = payload.summary;
        },

        [getEmployeeTimeoffOverview.rejected.type]: (state, action) => {
            state.isLoadingMobileCategoryOverview = false;
            state.mobileCategoryOverviewError = action.payload || action.error?.message || "Unable to load category overview.";
        },

        [getEmployeeTimeoffRequestsList.pending.type]: (state, action) => {
            if (isNextPage(action.meta?.arg?.page)) {
                state.isLoadingMoreMobileCategoryRequests = true;
            } else {
                state.isLoadingMobileCategoryRequests = true;
                state.mobileCategoryRequests = [];
                state.mobileCategoryRequestsMeta = undefined;
            }
            state.mobileCategoryRequestsError = undefined;
        },

        [getEmployeeTimeoffRequestsList.fulfilled.type]: (state, { payload }) => {
            const nextPage = isNextPage(payload.meta?.page);
            state.isLoadingMobileCategoryRequests = false;
            state.isLoadingMoreMobileCategoryRequests = false;
            state.mobileCategoryRequestsMeta = payload.meta;
            state.mobileCategoryRequests = nextPage
                ? mergeUniqueTimeoffs(state.mobileCategoryRequests, payload.records)
                : payload.records;
        },

        [getEmployeeTimeoffRequestsList.rejected.type]: (state, action) => {
            state.isLoadingMobileCategoryRequests = false;
            state.isLoadingMoreMobileCategoryRequests = false;
            state.mobileCategoryRequestsError = action.payload || action.error?.message || "Unable to load time off requests.";
        },

        [getEmployeeTimeoffBalanceHistoryList.pending.type]: (state, action) => {
            if (isNextPage(action.meta?.arg?.page)) {
                state.isLoadingMoreMobileCategoryBalanceHistory = true;
            } else {
                state.isLoadingMobileCategoryBalanceHistory = true;
                state.mobileCategoryBalanceHistory = [];
                state.mobileCategoryBalanceHistoryMeta = undefined;
            }
            state.mobileCategoryBalanceHistoryError = undefined;
        },

        [getEmployeeTimeoffBalanceHistoryList.fulfilled.type]: (state, { payload }) => {
            const nextPage = isNextPage(payload.meta?.page);
            state.isLoadingMobileCategoryBalanceHistory = false;
            state.isLoadingMoreMobileCategoryBalanceHistory = false;
            state.mobileCategoryBalanceHistoryMeta = payload.meta;
            state.mobileCategoryBalanceHistory = nextPage
                ? mergeUniqueBalanceHistory(state.mobileCategoryBalanceHistory, payload.records)
                : payload.records;
        },

        [getEmployeeTimeoffBalanceHistoryList.rejected.type]: (state, action) => {
            state.isLoadingMobileCategoryBalanceHistory = false;
            state.isLoadingMoreMobileCategoryBalanceHistory = false;
            state.mobileCategoryBalanceHistoryError =
                action.payload || action.error?.message || "Unable to load balance history.";
        },

        [getManagerTimeoffEmployees.pending.type]: (state) => {
            state.isFetchingManagerTimeoffEmployees = true;
            state.managerTimeoffEmployeeRecords = [];
        },

        [getManagerTimeoffEmployees.fulfilled.type]: (state, { payload }) => {
            state.isFetchingManagerTimeoffEmployees = false;
            state.managerTimeoffEmployeeRecords = payload;
        },

        [getManagerTimeoffEmployees.rejected.type]: (state) => {
            state.isFetchingManagerTimeoffEmployees = false;
            state.managerTimeoffEmployeeRecords = [];
        },

        [createTimeOffRequest.fulfilled.type]: (state) => {
            state.isCreatingTimeoffRequest = false;
        },

        [createTimeOffRequest.pending.type]: (state) => {
            state.isCreatingTimeoffRequest = true;
        },

        [createTimeOffRequest.rejected.type]: (state) => {
            state.isCreatingTimeoffRequest = false;
        },

        [updateTimeoffRequest.fulfilled.type]: (state) => {
            state.isUpdatingTimeoffRequest = false;
        },

        [updateTimeoffRequest.pending.type]: (state) => {
            state.isUpdatingTimeoffRequest = true;
        },

        [updateTimeoffRequest.rejected.type]: (state) => {
            state.isUpdatingTimeoffRequest = false;
        },

        [getTimeoffPolicyList.fulfilled.type]: (state, { payload }) => {
            state.isLoadingTimeoffPolicies = false;
            state.timeoffPolicies = payload;
        },

        [getTimeoffPolicyList.pending.type]: (state) => {
            state.isLoadingTimeoffPolicies = true;
            state.timeoffPolicies = [];
        },

        [getTimeoffPolicyList.rejected.type]: (state) => {
            state.isLoadingTimeoffPolicies = false;
            state.timeoffPolicies = [];
        },

        [getTimeoffEarningCodes.fulfilled.type]: (state, { payload }) => {
            state.isLoadingTimeoffEarningCodes = false;
            state.timeoffEarningCodes = payload;
        },

        [getTimeoffEarningCodes.pending.type]: (state) => {
            state.isLoadingTimeoffEarningCodes = true;
            state.timeoffEarningCodes = [];
        },

        [getTimeoffEarningCodes.rejected.type]: (state) => {
            state.isLoadingTimeoffEarningCodes = false;
            state.timeoffEarningCodes = [];
        },

        [updateTimeoffPolicy.fulfilled.type]: (state) => {
            state.isUpdatingTimeoff = false;
        },

        [updateTimeoffPolicy.pending.type]: (state) => {
            state.isUpdatingTimeoff = true;
        },

        [updateTimeoffPolicy.rejected.type]: (state) => {
            state.isUpdatingTimeoff = false;
        },

        [getTimeoffRequestDetails.fulfilled.type]: (state, action: PayloadAction<TimeoffRequest | undefined>) => {
            state.isLoadingTimeoffDetails = false;
            state.timeoffDetails = action.payload;
        },

        [getTimeoffRequestDetails.pending.type]: (state) => {
            state.isLoadingTimeoffDetails = true;
            state.timeoffDetails = undefined;
        },

        [getTimeoffRequestDetails.rejected.type]: (state) => {
            state.isLoadingTimeoffDetails = false;
            state.timeoffDetails = undefined;
        },
    },
});

export const {
    clearRequestDates,
    setRequestEndDate,
    setRequestStartDate,
    clearSelectedTimeoffForView,
    setSelectedTimeoffForView,
    clearTimeoffToUpdate,
    clearTimeoffUpdateMode,
    resetMobileTimeoffCategoryDetail,
    setTimeoffToUpdate,
    setTimeoffUpdateMode,
} = timeoffState.actions;
export default timeoffState.reducer;

//
